import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Send, Upload, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductDetailPage({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { product, categoryName } = location.state || {};

  const [formData, setFormData] = useState({
    companyName: '', fullName: '', email: '', phone: '',
    length: '', breadth: '', height: '', dimensionUnit: 'MM',
    ply: '3', gsm: ['', '', ''], moq: '',
    referenceImage: '', additionalNotes: ''
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const plyOptions = ['3', '5', '7', '9', '11'];
  const unitOptions = ['MM', 'INCHES', 'CMS'];

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={onAccentChange} />
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <Package className="w-16 h-16 text-muted-foreground" />
          <p className="text-muted-foreground">Product not found</p>
          <Button onClick={() => navigate('/products')} variant="outline"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Products</Button>
        </div>
        <Footer user={user} setUser={setUser} />
      </div>
    );
  }

  const handlePlyChange = (value) => {
    const plyCount = parseInt(value);
    setFormData({ ...formData, ply: value, gsm: Array(plyCount).fill('') });
  };

  const handleGsmChange = (index, value) => {
    const newGsm = [...formData.gsm];
    newGsm[index] = value;
    setFormData({ ...formData, gsm: newGsm });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/upload-public`, fd);
      const url = response.data.url.startsWith('http') ? response.data.url : `${BACKEND_URL}${response.data.url}`;
      setFormData({ ...formData, referenceImage: url });
      toast.success('Image uploaded!');
    } catch (error) { console.error('Upload failed:', error); toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.companyName || !formData.fullName || !formData.email || !formData.phone || !formData.length || !formData.breadth || !formData.height || !formData.moq) {
      toast.error('Please fill all mandatory fields'); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { toast.error('Invalid email'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-request`, {
        ...formData, productName: product.name, categoryName, productDescription: product.description,
      });
      toast.success('Quote request submitted!');
      setFormData({ companyName: '', fullName: '', email: '', phone: '', length: '', breadth: '', height: '', dimensionUnit: 'MM', ply: '3', gsm: ['', '', ''], moq: '', referenceImage: '', additionalNotes: '' });
    } catch (error) { console.error('Submission failed:', error); toast.error('Submission failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="product-detail-page">
      <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} />

      {/* Hero Banner */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button onClick={() => navigate('/products')} variant="ghost" className="mb-6 text-muted-foreground hover:text-foreground" data-testid="back-to-products">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Products
          </Button>
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight">{product.name}</h1>
              <p className="text-sm text-muted-foreground mt-1">{categoryName}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left - Product Details */}
          <div className="space-y-6">
            {product.image && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl overflow-hidden border border-border/30 shadow-lg">
                <img src={product.image} alt={product.name} className="w-full h-80 object-cover" />
              </motion.div>
            )}
            {product.video && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-2xl overflow-hidden border border-border/30 shadow-lg bg-black">
                <video src={product.video} controls className="w-full h-80 object-cover" />
              </motion.div>
            )}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-card rounded-2xl p-6 border border-border/30">
              <h3 className="text-lg font-bold text-foreground mb-3">Product Details</h3>
              {product.description && <p className="text-muted-foreground mb-3">{product.description}</p>}
              {product.specifications && (
                <div className="bg-background rounded-lg p-4 border border-border/20">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-1">Specifications</p>
                  <p className="text-sm text-foreground font-mono">{product.specifications}</p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Right - Quote Form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-card rounded-2xl p-6 border border-primary/20 shadow-lg h-fit sticky top-24" data-testid="quote-form">
            <h3 className="text-xl font-bold text-foreground mb-2">Request a Quote</h3>
            <p className="text-sm text-muted-foreground mb-6">Fill the form and we'll get back to you with the best pricing.</p>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-foreground">Company Name <span className="text-destructive">*</span></Label>
                  <Input value={formData.companyName} onChange={(e) => setFormData({...formData, companyName: e.target.value})} placeholder="Company" className="bg-background border-border/40 h-9 text-sm" data-testid="quote-company" />
                </div>
                <div>
                  <Label className="text-xs text-foreground">Full Name <span className="text-destructive">*</span></Label>
                  <Input value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} placeholder="Name" className="bg-background border-border/40 h-9 text-sm" data-testid="quote-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-foreground">Email <span className="text-destructive">*</span></Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="email@example.com" className="bg-background border-border/40 h-9 text-sm" data-testid="quote-email" />
                </div>
                <div>
                  <Label className="text-xs text-foreground">Phone <span className="text-destructive">*</span></Label>
                  <Input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+91..." className="bg-background border-border/40 h-9 text-sm" data-testid="quote-phone" />
                </div>
              </div>

              {/* Dimensions */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs text-foreground">Dimensions <span className="text-destructive">*</span></Label>
                  <div className="flex gap-1">
                    {unitOptions.map(unit => (
                      <button key={unit} type="button" onClick={() => setFormData({...formData, dimensionUnit: unit})}
                        className={`px-2 py-0.5 text-[10px] font-mono rounded font-semibold transition-colors ${formData.dimensionUnit === unit ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-primary/20'}`}>
                        {unit}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input type="number" value={formData.length} onChange={(e) => setFormData({...formData, length: e.target.value})} placeholder="L" className="bg-background border-border/40 h-9 text-sm font-mono" data-testid="quote-length" />
                  <Input type="number" value={formData.breadth} onChange={(e) => setFormData({...formData, breadth: e.target.value})} placeholder="B" className="bg-background border-border/40 h-9 text-sm font-mono" data-testid="quote-breadth" />
                  <Input type="number" value={formData.height} onChange={(e) => setFormData({...formData, height: e.target.value})} placeholder="H" className="bg-background border-border/40 h-9 text-sm font-mono" data-testid="quote-height" />
                </div>
              </div>

              {/* Ply + MOQ */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-foreground">Ply <span className="text-destructive">*</span></Label>
                  <select value={formData.ply} onChange={(e) => handlePlyChange(e.target.value)}
                    className="w-full px-3 py-2 h-9 text-sm border border-border/40 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary" data-testid="quote-ply">
                    {plyOptions.map(o => <option key={o} value={o}>{o} Ply</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-foreground">MOQ <span className="text-destructive">*</span></Label>
                  <Input type="number" value={formData.moq} onChange={(e) => setFormData({...formData, moq: e.target.value})} placeholder="Qty" className="bg-background border-border/40 h-9 text-sm font-mono" data-testid="quote-moq" />
                </div>
              </div>

              {/* GSM */}
              <div>
                <Label className="text-xs text-foreground mb-1 block">GSM (optional)</Label>
                <div className="grid grid-cols-3 gap-2">
                  {formData.gsm.slice(0, 3).map((value, index) => (
                    <Input key={index} type="number" value={value} onChange={(e) => handleGsmChange(index, e.target.value)} placeholder={`L${index+1}`} className="bg-background border-border/40 h-8 text-xs font-mono" />
                  ))}
                </div>
              </div>

              {/* Reference Image */}
              <div>
                <Label className="text-xs text-foreground mb-1 block">Reference Image</Label>
                <div className="border border-dashed border-border/60 rounded-lg p-3 text-center hover:border-primary/50 transition-colors">
                  <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" id="ref-upload-detail" disabled={uploading} />
                  <label htmlFor="ref-upload-detail" className="cursor-pointer flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    {uploading ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div> : <Upload className="w-4 h-4" />}
                    {uploading ? 'Uploading...' : 'Upload image (optional)'}
                  </label>
                </div>
                {formData.referenceImage && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={formData.referenceImage} alt="Ref" className="w-12 h-12 object-cover rounded border border-border/40" />
                    <button onClick={() => setFormData({...formData, referenceImage: ''})} className="text-xs text-destructive hover:underline">Remove</button>
                  </div>
                )}
              </div>

              {/* Notes */}
              <Textarea value={formData.additionalNotes} onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})} placeholder="Special requirements..." rows={2} className="bg-background border-border/40 text-sm" />

              <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="quote-submit-btn">
                <Send className="h-4 w-4 mr-2" /> {submitting ? 'Submitting...' : 'Submit Quote Request'}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer user={user} setUser={setUser} />
    </div>
  );
}
